#!/usr/bin/env node
// Load .env if present (dev convenience — production uses ~/.sherwood/config.json)
import { config as loadDotenv } from "dotenv";
try { loadDotenv(); } catch {};
import { createRequire } from "node:module";
const require = createRequire(import.meta.url);
// `CLI_VERSION_BUILD` is injected by tsup at build time (see tsup.config.ts) so
// the bundled/standalone binary never reads ../package.json at runtime — that
// path is unresolvable inside bun's compiled virtual filesystem and crashes the
// binary on startup. The require fallback keeps `tsx src/index.ts` (dev) working.
const CLI_VERSION: string =
  process.env.CLI_VERSION_BUILD || require("../package.json").version;
import { Command, Option } from "commander";
import { parseUnits, formatUnits, isAddress } from "viem";
import type { Address } from "viem";
import chalk from "chalk";
import ora from "ora";
import { input, confirm, select } from "@inquirer/prompts";
import { setNetwork, getNetwork, VALID_NETWORKS, DEFAULT_NETWORK, withNetwork } from "./lib/network.js";
import { getExplorerUrl, getChain } from "./lib/network.js";
import type { Network } from "./lib/network.js";
import { TOKENS } from "./lib/addresses.js";
import { getPublicClient, getAccount, formatContractError } from "./lib/client.js";
import { ERC20_ABI } from "./lib/abis.js";
import { parseBigIntArg } from "./lib/format.js";
import { MoonwellProvider } from "./providers/moonwell.js";
import { UniswapProvider } from "./providers/uniswap.js";
import { registerStrategyTemplateCommands } from "./commands/strategy-template.js";
import * as vaultLib from "./lib/vault.js";
import * as factoryLib from "./lib/factory.js";
import * as subgraphLib from "./lib/subgraph.js";
// registryLib removed — strategy registry is deprecated, replaced by template commands
import { uploadMetadata } from "./lib/ipfs.js";
import type { FundMetadata } from "./lib/ipfs.js";
import { registerVeniceCommands } from "./commands/venice.js";
import { registerAllowanceCommands } from "./commands/allowance.js";
import { registerIdentityCommands } from "./commands/identity.js";
import { registerProposalCommands } from "./commands/proposal.js";
import { registerQueueCommands } from "./commands/queue.js";
import { registerGovernorCommands } from "./commands/governor.js";
import { registerGuardianCommands } from "./commands/guardian-delegate.js";
import { setTextRecord, getTextRecord, resolveVaultFund, resolveFund, searchFundAcrossChains } from "./lib/ens.js";
import * as easLib from "./lib/eas.js";
import { EAS_SCHEMAS } from "./lib/addresses.js";
import {
  setCalldataOnly,
  isCalldataOnly,
  emitCalldata,
} from "./lib/calldata.js";
import {
  encodeIdentityMint,
  encodeJoinRequest,
  encodeApproveAgent,
  encodeDeposit,
  encodeRedeem,
  encodeCreateFund,
  encodeApproveDepositor,
  encodeRegisterAgent,
  type ChainId,
} from "@sherwoodagent/sdk";

// XMTP shells out to @xmtp/cli binary — lazy-load to avoid breaking
// non-chat commands if the CLI is not installed.
async function loadXmtp() {
  return import("./lib/xmtp.js");
}
// Lazy-load cron module (only needed for openclaw agents)
async function loadCron() {
  return import("./lib/cron.js");
}
import { cacheGroupId, getCachedGroupId, setChainContract, getChainContracts, loadConfig, setPrivateKey, getAgentId, setConfigRpcUrl, getNotifyTo, setNotifyTo, setUniswapApiKey, getUniswapApiKey, setVeniceApiKey, getVeniceApiKey, setAnthropicApiKey, addFund, getFunds, setPrimaryFund, getPrimaryFund } from "./lib/config.js";
import { isTestnet } from "./lib/network.js";

// ── Theme ──
const G = chalk.green;
const W = chalk.white;
const DIM = chalk.gray;
const BOLD = chalk.white.bold;
const LABEL = chalk.green.bold;
const SEP = () => console.log(DIM("─".repeat(60)));

function validateAddress(value: string, name: string): Address {
  if (!isAddress(value)) {
    console.error(chalk.red(`Invalid ${name} address: ${value}`));
    process.exit(1);
  }
  return value as Address;
}

/** Set vault address from --vault flag or fall back to config. */
function resolveVault(opts: { vault?: string }) {
  if (opts.vault) {
    vaultLib.setVaultAddress(validateAddress(opts.vault, "vault"));
  }
  // If no --vault flag, getVaultAddress() in vault.ts reads from config
}

const program = new Command();

program
  .name("sherwood")
  .description("CLI for agent-managed investment funds")
  .version(CLI_VERSION)
  .addOption(
    new Option("--chain <network>", "Target network")
      .choices(VALID_NETWORKS)
      .default(DEFAULT_NETWORK),
  )
  .option(
    "--calldata-only",
    "Print unsigned EIP-5792 calldata as JSON instead of signing/sending (for external signers — no private key required)",
    false,
  )
  .hook("preAction", (thisCommand) => {
    const opts = thisCommand.optsWithGlobals();
    setCalldataOnly(Boolean(opts.calldataOnly));
    setNetwork(opts.chain as Network);
  });

// ── Fund commands ──
const fund = program.command("fund").alias("syndicate");

fund
  .command("create")
  .description("Create a new fund via the factory (interactive)")
  .option("--subdomain <name>", "ENS subdomain (skip prompt)")
  .option("--name <name>", "Fund name (skip prompt)")
  .option("--agent-id <id>", "ERC-8004 agent identity token ID (skip prompt)")
  .option("--asset <symbol-or-address>", "Vault asset: USDC, WETH, or a token address")
  .option("--description <text>", "Short description")
  .option("--metadata-uri <uri>", "Override metadata URI (skip IPFS upload)")
  .option("--open-deposits", "Allow anyone to deposit (default: whitelist only)")
  .option("--public-chat", "Enable dashboard spectator mode", false)
  .option("-y, --yes", "Skip confirmation prompt (non-interactive mode)", false)
  .action(async (opts) => {
    try {
      // ── Header ──
      console.log();
      console.log(LABEL("  ◆ Create Fund"));
      SEP();

      // --calldata-only has no wallet to display (no private key); show network only.
      if (!isCalldataOnly()) {
        const wallet = getAccount();
        console.log(DIM(`  Wallet:  ${wallet.address}`));
      }
      console.log(DIM(`  Network: ${getChain().name}`));
      SEP();

      // ── Gather inputs (prompt if not provided via flags) ──

      const savedAgentId = getAgentId();

      const nonInteractive = opts.yes;

      const name = opts.name || (nonInteractive
        ? (() => { throw new Error("--name is required in non-interactive mode (-y)"); })()
        : await input({
            message: G("Fund name"),
            validate: (v: string) => v.length > 0 || "Name is required",
          }));

      const subdomain = opts.subdomain || (nonInteractive
        ? name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")
        : await input({
            message: G("ENS subdomain"),
            default: name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
            validate: (v: string) => v.length >= 3 || "Must be at least 3 characters",
          }));

      const description = opts.description || (nonInteractive
        ? `${name} — a Sherwood fund`
        : await input({
            message: G("Description"),
            default: `${name} — a Sherwood fund`,
          }));

      const agentIdStr = opts.agentId || (savedAgentId
        ? (nonInteractive
            ? String(savedAgentId)
            : await input({ message: G("Agent ID (ERC-8004)"), default: String(savedAgentId) }))
        : (nonInteractive
            ? "0"
            : await input({ message: G("Agent ID (ERC-8004)"), validate: (v: string) => /^\d+$/.test(v) || "Must be a number" }))
      );

      const openDeposits = opts.openDeposits !== undefined ? opts.openDeposits : (nonInteractive
        ? false
        : await confirm({
            message: G("Open deposits? (anyone can deposit, no whitelist)"),
            default: false,
          }));

      // ── Resolve asset ──
      // Supported symbols (testnet + mainnet). Will expand on mainnet launch.
      const ASSET_SYMBOLS: Record<string, Address> = {
        USDC: TOKENS().USDC,
        WETH: TOKENS().WETH,
      };

      let asset: Address;
      if (opts.asset) {
        const upper = opts.asset.toUpperCase();
        if (ASSET_SYMBOLS[upper]) {
          asset = ASSET_SYMBOLS[upper];
        } else if (opts.asset.startsWith("0x") && opts.asset.length === 42) {
          asset = opts.asset as Address;
        } else {
          const supported = Object.keys(ASSET_SYMBOLS).join(", ");
          console.error(chalk.red(`  Unknown asset "${opts.asset}". Use a symbol (${supported}) or a 0x address.`));
          process.exit(1);
        }
      } else if (nonInteractive) {
        // Default to WETH on chains without USDC, otherwise USDC
        asset = TOKENS().USDC !== "0x0000000000000000000000000000000000000000"
          ? ASSET_SYMBOLS.USDC
          : ASSET_SYMBOLS.WETH;
      } else {
        // Interactive prompt
        const assetChoice = await select({
          message: G("Vault asset (what token do depositors provide?)"),
          choices: [
            { name: "USDC", value: "USDC", description: "USD Coin (6 decimals)" },
            { name: "WETH", value: "WETH", description: "Wrapped Ether (18 decimals)" },
          ],
        });
        asset = ASSET_SYMBOLS[assetChoice];
      }

      const publicClient = getPublicClient();
      const [decimals, assetSymbol] = await Promise.all([
        publicClient.readContract({ address: asset, abi: ERC20_ABI, functionName: "decimals" }) as Promise<number>,
        publicClient.readContract({ address: asset, abi: ERC20_ABI, functionName: "symbol" }) as Promise<string>,
      ]);
      const symbol = `sw${assetSymbol}`;

      // ── Confirmation ──
      console.log();
      console.log(LABEL("  ◆ Review"));
      SEP();
      console.log(W(`  Name:         ${BOLD(name)}`));
      console.log(W(`  ENS:          ${G(`${subdomain}.sherwoodagent.eth`)}`));
      console.log(W(`  Description:  ${DIM(description)}`));
      console.log(W(`  Agent ID:     #${agentIdStr}`));
      console.log(W(`  Asset:        ${assetSymbol} (${asset.slice(0, 10)}...)`));
      console.log(W(`  Share token:  ${symbol}`));
      console.log(W(`  Open deposits: ${openDeposits ? chalk.yellow("yes (anyone can deposit)") : G("no (whitelist)")}`));
      SEP();

      if (!nonInteractive) {
        const go = await confirm({ message: G("Deploy fund?"), default: true });
        if (!go) {
          console.log(DIM("  Cancelled."));
          return;
        }
      }

      // ── Upload metadata to IPFS ──
      let metadataURI = opts.metadataUri || "";

      if (!metadataURI) {
        const spinner = ora({ text: W("Uploading metadata to IPFS..."), color: "green" }).start();
        try {
          const metadata: FundMetadata = {
            schema: "sherwood/fund/v1",
            name,
            description,
            chain: getChain().name,
            strategies: [],
            terms: {
              feeModel: "none",
            },
            links: {},
          };
          metadataURI = await uploadMetadata(metadata);
          spinner.succeed(G(`Metadata pinned: ${DIM(metadataURI)}`));
        } catch (err) {
          spinner.warn(chalk.yellow(`IPFS upload failed — using inline metadata`));
          const json = JSON.stringify({ name, description, subdomain, asset: assetSymbol, openDeposits, createdBy: "@sherwoodagent/cli" });
          metadataURI = `data:application/json;base64,${Buffer.from(json).toString("base64")}`;
        }
      }

      // --calldata-only: emit the unsigned createFund calldata and stop,
      // before the subdomain idempotency check (which reads getAccount()).
      // Owner-stake bonding (guardianRegistry.prepareOwnerStake) is a separate
      // prerequisite — see the action note.
      if (isCalldataOnly()) {
        emitCalldata(
          encodeCreateFund(
            {
              creatorAgentId: agentIdStr,
              metadataURI,
              asset,
              name,
              symbol,
              openDeposits,
              subdomain,
            },
            getChain().id as ChainId,
          ),
        );
        return;
      }

      // ── Idempotent check: resume if subdomain already exists ──
      const existingId = await factoryLib.subdomainExists(subdomain);
      if (existingId !== null) {
        const existingInfo = await factoryLib.getFund(existingId);
        const callerAddress = getAccount().address.toLowerCase();

        if (existingInfo.creator.toLowerCase() !== callerAddress) {
          console.error(chalk.red(`\n  ✖ Subdomain '${subdomain}' is already taken by another fund.\n`));
          process.exit(1);
        }

        // Partial completion — creator already deployed, resume post-deploy steps
        console.log(chalk.yellow(`\n  ⚠ Fund '${subdomain}' already exists — resuming setup...\n`));

        const spinner = ora({ text: W("Resuming setup..."), color: "green" }).start();

        // Save to config
        const chainId = getChain().id;
        setChainContract(chainId, "vault", existingInfo.vault);
        addFund(chainId, { subdomain, vault: existingInfo.vault, role: "creator" });
        setPrimaryFund(chainId, subdomain);

        // Register creator as agent (if not already)
        spinner.text = W("Checking agent registration...");
        try {
          vaultLib.setVaultAddress(existingInfo.vault);
          const isAlreadyAgent = await vaultLib.isAgent(getAccount().address);
          if (!isAlreadyAgent) {
            spinner.text = W("Registering creator as agent...");
            await vaultLib.registerAgent(BigInt(agentIdStr), getAccount().address);
          }
        } catch { /* non-fatal */ }

        // Create XMTP group (if not already)
        spinner.text = W("Checking chat group...");
        try {
          const xmtp = await loadXmtp();
          const xmtpClient = await xmtp.getXmtpClient();
          try {
            await xmtp.getGroup(xmtpClient, subdomain);
            // Group exists, skip
          } catch {
            // No group — create one
            spinner.text = W("Creating chat group...");
            const groupId = await xmtp.createFundGroup(xmtpClient, subdomain, opts.publicChat);
            await setTextRecord(subdomain, "xmtpGroupId", groupId, existingInfo.vault as Address);
            cacheGroupId(subdomain, groupId);
          }
        } catch {
          console.warn(chalk.yellow("\n  ⚠ Could not set up XMTP chat group"));
        }

        // Register crons
        try {
          const cron = await loadCron();
          cron.registerFundCrons(subdomain, isTestnet(), getNotifyTo());
        } catch { /* non-fatal */ }

        spinner.stop();
        console.log();
        console.log(LABEL("  ◆ Fund Setup Resumed"));
        SEP();
        console.log(W(`  ID:       ${G(`#${existingId}`)}`));
        console.log(W(`  Vault:    ${G(existingInfo.vault)}`));
        console.log(W(`  ENS:      ${G(`${subdomain}.sherwoodagent.eth`)}`));
        console.log(W(`  Chat:     ${DIM(`sherwood chat ${subdomain}`)}`));
        const refParam = getAgentId() ? `?ref=${getAgentId()}` : "";
        console.log(W(`  Share:    ${G(`https://sherwood.sh/fund/${subdomain}${refParam}`)}`));
        SEP();
        console.log(G("  ✓ Fund saved to ~/.sherwood/config.json (set as primary)"));
        console.log();
        return;
      }

      // ── Deploy (fresh fund) ──
      const spinner = ora({ text: W("Deploying vault via factory..."), color: "green" }).start();

      const result = await factoryLib.createFund({
        creatorAgentId: BigInt(agentIdStr),
        metadataURI,
        asset,
        name,
        symbol,
        openDeposits,
        subdomain,
      });

      // Auto-save vault address + fund membership to config
      const chainId = getChain().id;
      setChainContract(chainId, "vault", result.vault);
      addFund(chainId, { subdomain, vault: result.vault, role: "creator" });
      setPrimaryFund(chainId, subdomain);

      // ── Register creator as agent on the vault ──
      // Brief delay to let the RPC node sync its nonce after createFund tx
      await new Promise((r) => setTimeout(r, 2000));
      spinner.text = W("Registering creator as agent...");
      try {
        vaultLib.setVaultAddress(result.vault);
        const creatorAddress = getAccount().address;
        await vaultLib.registerAgent(
          BigInt(agentIdStr),
          creatorAddress,        // agentAddress = creator EOA (direct execution)
        );
      } catch (regErr) {
        // Non-fatal — creator can register later via `fund add`
        console.warn(chalk.yellow("\n  ⚠ Could not auto-register creator as agent — register manually with `fund add`"));
      }

      spinner.text = W("Setting up chat...");

      // Create XMTP group for fund chat
      try {
        const xmtp = await loadXmtp();
        const xmtpClient = await xmtp.getXmtpClient();
        const groupId = await xmtp.createFundGroup(xmtpClient, subdomain, opts.publicChat);
        cacheGroupId(subdomain, groupId);
        try {
          await setTextRecord(subdomain, "xmtpGroupId", groupId, result.vault as Address);
        } catch { /* ENS not available on all chains */ }
      } catch {
        console.warn(chalk.yellow("\n  ⚠ Could not create XMTP chat group"));
        console.warn(chalk.dim(`    Recover later with: sherwood chat ${subdomain} init`));
      }

      // ── Auto-register participation crons ──
      try {
        const cron = await loadCron();
        const cronResult = cron.registerFundCrons(subdomain, isTestnet(), getNotifyTo());
        if (cronResult.isOpenClaw && cronResult.registered) {
          console.log(G("  ✓ Participation crons registered (15m check + hourly summary)"));
        } else if (!cronResult.isOpenClaw) {
          console.log(DIM("  Tip: Set up a scheduled process to run `sherwood session check " + subdomain + "` periodically"));
        }
      } catch { /* non-fatal */ }

      spinner.stop();

      // ── Success ──
      console.log();
      console.log(LABEL("  ◆ Fund Created"));
      SEP();
      console.log(W(`  ID:       ${G(`#${result.fundId}`)}`));
      console.log(W(`  Vault:    ${G(result.vault)}`));
      console.log(W(`  ENS:      ${G(`${subdomain}.sherwoodagent.eth`)}`));
      console.log(W(`  Metadata: ${DIM(metadataURI.length > 50 ? metadataURI.slice(0, 50) + "..." : metadataURI)}`));
      console.log(W(`  Explorer: ${DIM(getExplorerUrl(result.hash))}`));
      console.log(W(`  Chat:     ${DIM(`sherwood chat ${subdomain}`)}`));
      const refParam = getAgentId() ? `?ref=${getAgentId()}` : "";
      console.log(W(`  Share:    ${G(`https://sherwood.sh/fund/${subdomain}${refParam}`)}`));
      SEP();
      console.log(G("  ✓ Fund saved to ~/.sherwood/config.json (set as primary)"));
      console.log();
    } catch (err) {
      console.error(chalk.red(`\n  ✖ ${formatContractError(err)}`));
      process.exit(1);
    }
  });

fund
  .command("list")
  .description("List active funds (queries subgraph, falls back to on-chain)")
  .option("--creator <address>", "Filter by creator address")
  .option("--all-chains", "Aggregate active funds across all supported chains (on-chain only)")
  .action(async (opts) => {
    const spinner = ora("Loading funds...").start();
    try {
      // Each row carries an optional `chain` so the --all-chains output can
      // tell users which network a fund lives on.
      type Row = {
        id: string | bigint;
        vault: string;
        creator: string;
        metadataURI: string;
        createdAt: string | bigint;
        totalDeposits?: string;
        totalWithdrawals?: string;
        subdomain?: string;
        chain?: Network;
      };
      let funds: Row[] = [];

      if (opts.allChains) {
        // Subgraph is single-chain; on-chain only when iterating networks.
        // Current deployment target is a single chain, so this iterates one net.
        const targets = VALID_NETWORKS;
        for (const net of targets) {
          spinner.text = `Loading from ${net}...`;
          try {
            const rows = await withNetwork(net, () => factoryLib.getActiveFunds());
            for (const s of rows) {
              funds.push({
                id: s.id.toString(),
                vault: s.vault,
                creator: s.creator,
                metadataURI: s.metadataURI,
                createdAt: s.createdAt.toString(),
                subdomain: s.subdomain,
                chain: net,
              });
            }
          } catch {
            // Skip flaky chain RPCs rather than failing the whole listing.
          }
        }
        if (opts.creator) {
          const c = String(opts.creator).toLowerCase();
          funds = funds.filter((s) => s.creator.toLowerCase() === c);
        }
      } else if (process.env.SUBGRAPH_URL) {
        const result = await subgraphLib.getActiveFunds(opts.creator);
        funds = result;
      } else {
        const result = await factoryLib.getActiveFunds();
        funds = result.map((s) => ({
          id: s.id.toString(),
          vault: s.vault,
          creator: s.creator,
          metadataURI: s.metadataURI,
          createdAt: s.createdAt.toString(),
          subdomain: s.subdomain,
        }));
      }

      spinner.stop();

      if (funds.length === 0) {
        console.log(chalk.dim("No active funds found."));
        return;
      }

      console.log();
      console.log(chalk.bold(`Active Funds (${funds.length})`));
      if (!process.env.SUBGRAPH_URL && !opts.allChains) {
        console.log(chalk.dim("  (Set SUBGRAPH_URL for faster indexed queries)"));
      }
      if (opts.allChains) {
        console.log(chalk.dim("  (Aggregated across chains via on-chain factory calls; SUBGRAPH_URL is single-chain only)"));
      }
      console.log(chalk.dim("─".repeat(70)));

      for (const s of funds) {
        const ts = typeof s.createdAt === "string" ? Number(s.createdAt) : Number(s.createdAt);
        const date = new Date(ts * 1000).toLocaleDateString();
        const ensName = s.subdomain ? `${s.subdomain}.sherwoodagent.eth` : "";
        const chainTag = s.chain ? chalk.dim(`[${s.chain}]  `) : "";
        console.log(`  ${chainTag}#${s.id}  ${chalk.bold(ensName || String(s.vault))}`);
        if (ensName) console.log(`    Vault:   ${chalk.cyan(String(s.vault))}`);
        console.log(`    Creator: ${s.creator}`);
        console.log(`    Created: ${date}`);
        if (s.totalDeposits) {
          console.log(`    Deposits: ${s.totalDeposits} USDC`);
        }
        if (s.metadataURI) {
          console.log(`    Metadata: ${chalk.dim(s.metadataURI)}`);
        }
        console.log();
      }
    } catch (err) {
      spinner.fail("Failed to load funds");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("info")
  .description("Display fund details by ID or subdomain")
  .argument("<id>", "Fund ID or subdomain name")
  .action(async (idStr) => {
    const spinner = ora("Loading fund info...").start();
    try {
      let id: bigint;
      let crossChainHint: { network: Network; others: Network[] } | null = null;
      let infoOverride: factoryLib.FundInfo | null = null;

      if (/^\d+$/.test(idStr)) {
        id = BigInt(idStr);
      } else {
        // Try active chain first; on miss, scan all enabled chains so users
        // who default to base can still inspect a HyperEVM fund.
        try {
          const resolved = await resolveFund(idStr);
          id = resolved.id;
        } catch {
          spinner.text = `Searching across chains...`;
          const hits = await searchFundAcrossChains(idStr);
          if (hits.length === 0) {
            spinner.fail(`Fund "${idStr}" not found on any supported chain`);
            process.exit(1);
          }
          // Prefer active chain hit, else first hit.
          const active = getNetwork();
          const primary = hits.find((h) => h.network === active) ?? hits[0];
          id = primary.result.id;
          // Read the fund from the chain it actually lives on (NOT the
          // active chain) so vault stats etc. don't blow up trying to read a
          // non-existent vault on the active chain.
          infoOverride = await withNetwork(primary.network, () =>
            factoryLib.getFund(primary.result.id),
          );
          crossChainHint = {
            network: primary.network,
            others: hits.filter((h) => h.network !== primary.network).map((h) => h.network),
          };
        }
      }
      const info = infoOverride ?? (await factoryLib.getFund(id));
      spinner.stop();
      if (crossChainHint) {
        const others = crossChainHint.others.length > 0
          ? ` (also on: ${crossChainHint.others.join(", ")})`
          : "";
        console.log(chalk.yellow(`  Found on chain: ${chalk.bold(crossChainHint.network)}${others}`));
        console.log(chalk.dim(`  Re-run with --chain ${crossChainHint.network} to interact with this fund.`));
        console.log();
      }

      if (!info.vault || info.vault === "0x0000000000000000000000000000000000000000") {
        console.log(chalk.red(`Fund #${id} not found.`));
        process.exit(1);
      }

      const date = new Date(Number(info.createdAt) * 1000).toLocaleDateString();
      console.log();
      console.log(chalk.bold(`Fund #${info.id}`));
      console.log(chalk.dim("─".repeat(40)));
      if (info.subdomain) {
        console.log(`  ENS:        ${chalk.bold(`${info.subdomain}.sherwoodagent.eth`)}`);
      }
      console.log(`  Vault:      ${chalk.cyan(info.vault)}`);
      console.log(`  Creator:    ${info.creator}`);
      console.log(`  Created:    ${date}`);
      console.log(`  Active:     ${info.active ? chalk.green("yes") : chalk.red("no")}`);
      if (info.metadataURI) {
        console.log(`  Metadata:   ${chalk.dim(info.metadataURI)}`);
      }

      // Show XMTP group ID if cached
      if (info.subdomain) {
        const xmtpGroupId = getCachedGroupId(info.subdomain);
        if (xmtpGroupId) {
          console.log(`  XMTP Group: ${chalk.cyan(xmtpGroupId)}`);
        }
      }

      // Also show vault info — when the fund lives on a different
      // chain than the active one, read the vault on its actual chain.
      vaultLib.setVaultAddress(info.vault);
      const vaultInfo = crossChainHint
        ? await withNetwork(crossChainHint.network, () => vaultLib.getVaultInfo())
        : await vaultLib.getVaultInfo();
      console.log();
      console.log(chalk.bold("  Vault Stats"));
      console.log(`    Total Assets:       ${vaultInfo.totalAssets}`);
      console.log(`    Agent Count:        ${vaultInfo.agentCount}`);
      console.log(`    Redemptions Locked: ${vaultInfo.redemptionsLocked}`);
      console.log(`    Management Fee:     ${Number(vaultInfo.managementFeeBps) / 100}%`);
      console.log();
    } catch (err) {
      spinner.fail("Failed to load fund info");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("update-metadata")
  .description("Update fund metadata (creator only)")
  .requiredOption("--id <id>", "Fund ID")
  .option("--name <name>", "Fund name")
  .option("--description <text>", "Short description")
  .option("--uri <uri>", "Direct metadata URI (skips IPFS upload)")
  .action(async (opts) => {
    const spinner = ora({ text: W("Loading fund..."), color: "green" }).start();
    try {
      const fundId = BigInt(opts.id);
      let metadataURI = opts.uri;

      if (!metadataURI) {
        const info = await factoryLib.getFund(fundId);
        if (!info.vault || info.vault === "0x0000000000000000000000000000000000000000") {
          spinner.fail(`Fund #${opts.id} not found.`);
          process.exit(1);
        }

        const name = opts.name || info.subdomain;
        const description = opts.description || `${name} — a Sherwood fund on ${info.subdomain}.sherwoodagent.eth`;

        spinner.text = W("Uploading metadata to IPFS...");
        const metadata: FundMetadata = {
          schema: "sherwood/fund/v1",
          name,
          description,
          chain: getChain().name,
          strategies: [],
          terms: {},
          links: {},
        };
        metadataURI = await uploadMetadata(metadata);
        spinner.text = W("Updating on-chain metadata...");
      }

      const hash = await factoryLib.updateMetadata(fundId, metadataURI);
      spinner.succeed(G(`Metadata updated`));
      console.log(DIM(`  IPFS: ${metadataURI}`));
      console.log(DIM(`  ${getExplorerUrl(hash)}`));
    } catch (err) {
      spinner.fail("Metadata update failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("approve-depositor")
  .description("Approve an address to deposit (owner only)")
  .option("--vault <address>", "Vault address (default: from config)")
  .requiredOption("--depositor <address>", "Address to approve")
  .action(async (opts) => {
    resolveVault(opts);
    const depositor = validateAddress(opts.depositor, "depositor");
    if (isCalldataOnly()) {
      emitCalldata(
        encodeApproveDepositor(
          { vault: vaultLib.getVaultAddress(), depositor },
          getChain().id as ChainId,
        ),
      );
      return;
    }
    const spinner = ora("Approving depositor...").start();
    try {
      const hash = await vaultLib.approveDepositor(depositor);
      spinner.succeed(`Depositor approved: ${hash}`);
      console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
    } catch (err) {
      spinner.fail("Approval failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("remove-depositor")
  .description("Remove an address from the depositor whitelist (owner only)")
  .option("--vault <address>", "Vault address (default: from config)")
  .requiredOption("--depositor <address>", "Address to remove")
  .action(async (opts) => {
    resolveVault(opts);
    const spinner = ora("Removing depositor...").start();
    try {
      const depositor = validateAddress(opts.depositor, "depositor");
      const hash = await vaultLib.removeDepositor(depositor);
      spinner.succeed(`Depositor removed: ${hash}`);
      console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
    } catch (err) {
      spinner.fail("Removal failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("set-agent-fee")
  .description("Set the vault's agent performance fee in bps (vault owner only)")
  .option("--vault <address>", "Vault address (default: from config)")
  // The 1500 below mirrors the on-chain MAX_AGENT_FEE_BPS
  // (contracts/src/FeeConstants.sol) — keep this help text in sync if the cap changes.
  .requiredOption(
    "--bps <bps>",
    "Agent fee in bps (e.g. 1000 = 10%; max 1500 = 15%). The governor clamps the realized fee to its configured maxPerformanceFeeBps, which may be lower.",
  )
  .action(async (opts) => {
    resolveVault(opts);
    const spinner = ora("Setting agent fee...").start();
    try {
      // Bounds (≤ MAX_AGENT_FEE_BPS) are enforced by the contract's
      // `AgentFeeTooHigh` revert — no need to duplicate the cap here.
      const bps = parseBigIntArg(opts.bps, "bps");
      const hash = await vaultLib.setAgentFeeBps(bps);
      spinner.succeed(
        `Agent fee set to ${Number(bps) / 100}% (realized fee is clamped to the governor's maxPerformanceFeeBps): ${hash}`,
      );
      console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
    } catch (err) {
      spinner.fail("Failed to set agent fee");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("add")
  .description("Register an agent on a fund vault (creator only)")
  .option("--vault <address>", "Vault address (default: from config)")
  .option("--agent-id <id>", "Agent's ERC-8004 identity token ID (resolved from wallet if omitted)")
  .requiredOption("--wallet <address>", "Agent wallet address")
  .action(async (opts) => {
    // --calldata-only: emit registerAgent calldata. Requires --agent-id (the
    // wallet-based auto-lookup is skipped in calldata mode) and runs before any
    // signer-dependent path.
    if (isCalldataOnly()) {
      resolveVault(opts);
      const agentWallet = validateAddress(opts.wallet, "wallet");
      if (!opts.agentId) {
        console.error(
          chalk.red(
            "--calldata-only requires --agent-id (auto-lookup from wallet is skipped in calldata mode)",
          ),
        );
        process.exit(1);
      }
      emitCalldata(
        encodeRegisterAgent(
          {
            vault: vaultLib.getVaultAddress(),
            agentAddress: agentWallet,
            agentId: BigInt(opts.agentId),
          },
          getChain().id as ChainId,
        ),
      );
      return;
    }

    const spinner = ora("Verifying creator...").start();
    try {
      // Resolve vault address from --vault flag or config
      resolveVault(opts);
      const vaultAddress = vaultLib.getVaultAddress();

      // Verify caller is the fund creator
      const { creator, subdomain } = await resolveVaultFund(vaultAddress);
      const callerAddress = getAccount().address.toLowerCase();
      if (creator.toLowerCase() !== callerAddress) {
        spinner.fail("Only the fund creator can add agents");
        process.exit(1);
      }

      const agentWallet = validateAddress(opts.wallet, "wallet");

      // Resolve agent ID: use --agent-id if provided, otherwise look up from wallet
      let agentId: bigint;
      if (opts.agentId) {
        agentId = BigInt(opts.agentId);
      } else {
        const { AGENT_REGISTRY } = await import("./lib/addresses.js");
        const registry = AGENT_REGISTRY().IDENTITY_REGISTRY;

        // Chains without ERC-8004 (e.g. Robinhood testnet) — contract skips identity check
        if (registry === "0x0000000000000000000000000000000000000000") {
          agentId = 0n;
        } else {
          spinner.text = "Looking up ERC-8004 identity from wallet...";
          const client = getPublicClient();

          const balance = await client.readContract({
            address: registry,
            abi: [{
              name: "balanceOf",
              type: "function",
              stateMutability: "view",
              inputs: [{ name: "owner", type: "address" }],
              outputs: [{ name: "", type: "uint256" }],
            }] as const,
            functionName: "balanceOf",
            args: [agentWallet],
          }) as bigint;

          if (balance === 0n) {
            spinner.fail("Agent wallet does not own an ERC-8004 identity NFT");
            console.error(chalk.dim("  Mint one first: sherwood identity mint --name <name>"));
            process.exit(1);
          }

          const tokenId = await client.readContract({
            address: registry,
            abi: [{
              name: "tokenOfOwnerByIndex",
              type: "function",
              stateMutability: "view",
              inputs: [{ name: "owner", type: "address" }, { name: "index", type: "uint256" }],
              outputs: [{ name: "", type: "uint256" }],
            }] as const,
            functionName: "tokenOfOwnerByIndex",
            args: [agentWallet, 0n],
          }) as bigint;

          agentId = tokenId;
          console.log(chalk.dim(`  Resolved agent ID: #${agentId}`));
        }
      }

      spinner.text = "Registering agent...";
      const hash = await vaultLib.registerAgent(agentId, agentWallet);
      spinner.succeed(`Agent registered: ${hash}`);
      console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));

      // Auto-add agent to XMTP chat group
      try {
        const xmtp = await loadXmtp();
        const xmtpClient = await xmtp.getXmtpClient();
        const group = await xmtp.getGroup(xmtpClient, subdomain);
        await xmtp.addMember(group, opts.wallet);
        await xmtp.sendEnvelope(group, {
          type: "AGENT_REGISTERED",
          agent: { erc8004Id: Number(agentId), address: opts.wallet },
          fund: subdomain,
          timestamp: Math.floor(Date.now() / 1000),
        });
        console.log(chalk.dim(`  Added to chat: ${subdomain}`));
      } catch {
        console.warn(chalk.yellow("  ⚠ Could not add agent to chat group"));
        console.warn(chalk.dim(`    If no group exists, run: sherwood chat ${subdomain} init`));
      }
    } catch (err) {
      spinner.fail("Registration failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("share")
  .description("Print the shareable dashboard URL for your fund")
  .option("--subdomain <name>", "Fund subdomain (default: primary)")
  .action(async (opts) => {
    try {
      const subdomain = opts.subdomain || getPrimaryFund(getChain().id)?.subdomain;
      if (!subdomain) {
        console.error(chalk.red("  No fund configured. Pass --subdomain <name> or run 'fund create/join' first."));
        process.exit(1);
      }

      // Verify fund exists on-chain
      const fundInfo = await resolveFund(subdomain);

      const agentId = getAgentId();
      const refParam = agentId ? `?ref=${agentId}` : "";
      const dashboardUrl = `https://sherwood.sh/fund/${subdomain}${refParam}`;

      console.log();
      console.log(LABEL("  ◆ Share Your Fund"));
      SEP();
      console.log(W(`  Dashboard:  ${G(dashboardUrl)}`));
      console.log(W(`  ENS:        ${DIM(`${subdomain}.sherwoodagent.eth`)}`));
      console.log(W(`  Vault:      ${DIM(fundInfo.vault)}`));
      console.log(W(`  Join CLI:   ${DIM(`sherwood fund join --subdomain ${subdomain}`)}`));
      SEP();

      // Try to copy to clipboard (optional — uses pbcopy/xclip if available)
      try {
        const { exec } = await import("node:child_process");
        const platform = (await import("node:os")).platform();
        const cmd = platform === "darwin" ? "pbcopy" : "xclip -selection clipboard";
        const proc = exec(cmd);
        proc.stdin?.write(dashboardUrl);
        proc.stdin?.end();
        console.log(G("  ✓ Dashboard URL copied to clipboard"));
      } catch {
        // clipboard not available — no problem
      }
      console.log();
    } catch (err) {
      console.error(chalk.red(`  ✖ ${formatContractError(err)}`));
      process.exit(1);
    }
  });

// ── EAS Join Request / Approval Commands ──

fund
  .command("join")
  .description("Request to join a fund (creates an EAS attestation)")
  .option("--subdomain <name>", "Fund subdomain to join")
  .option("--ref <agentId>", "Referrer agent ID (from invite link)")
  .option("--message <text>", "Message to the creator", "Requesting to join your fund")
  .action(async (opts) => {
    const spinner = ora("Resolving fund...").start();
    try {
      const subdomain = opts.subdomain || getPrimaryFund(getChain().id)?.subdomain;
      if (!subdomain) {
        spinner.fail("No fund configured. Pass --subdomain <name> or run 'fund create' first.");
        process.exit(1);
      }

      const agentId = getAgentId();
      if (!agentId) {
        spinner.fail("No agent identity found. Run 'sherwood identity mint' first.");
        process.exit(1);
      }

      let fund: Awaited<ReturnType<typeof resolveFund>>;
      try {
        fund = await resolveFund(subdomain);
      } catch {
        // Active chain doesn't have it — see if it lives elsewhere.
        spinner.text = "Searching across chains...";
        const hits = await searchFundAcrossChains(subdomain);
        spinner.fail(`Fund "${subdomain}" not found on ${getNetwork()}`);
        if (hits.length === 0) {
          console.error(chalk.red(`  Not found on any supported chain.`));
        } else if (hits.length === 1) {
          console.error(chalk.yellow(`  Found on chain: ${chalk.bold(hits[0].network)}`));
          console.error(chalk.dim(`  Re-run: sherwood --chain ${hits[0].network} fund join --subdomain ${subdomain}`));
        } else {
          const chains = hits.map((h) => h.network).join(", ");
          console.error(chalk.yellow(`  Found on chains: ${chalk.bold(chains)}`));
          console.error(chalk.dim(`  Specify which chain to join with --chain <network>.`));
        }
        process.exit(1);
      }

      // --calldata-only: emit the unsigned join-request attestation and stop
      // before any signer-dependent path (membership checks read getAccount()).
      if (isCalldataOnly()) {
        const referrerAgentId = opts.ref ? parseInt(opts.ref, 10) : undefined;
        spinner.stop();
        emitCalldata(
          encodeJoinRequest(
            {
              fundId: fund.id,
              agentId: BigInt(agentId),
              vault: fund.vault,
              creator: fund.creator,
              message: opts.message,
              referrerAgentId,
            },
            getChain().id as ChainId,
          ),
        );
        return;
      }

      const callerAddress = getAccount().address;

      // Check if already registered as an agent on this vault
      spinner.text = "Checking membership...";
      vaultLib.setVaultAddress(fund.vault);
      const alreadyAgent = await vaultLib.isAgent(callerAddress);
      if (alreadyAgent) {
        spinner.succeed("You are already a registered agent on this fund");
        // Still ensure XMTP identity is ready
        try {
          const xmtp = await loadXmtp();
          await xmtp.getXmtpClient();
          console.log(chalk.dim("  XMTP identity ready"));
        } catch {
          console.warn(chalk.yellow("  ⚠ Could not initialize XMTP identity"));
        }
        // Auto-register participation crons (idempotent)
        try {
          const cron = await loadCron();
          const cronResult = cron.registerFundCrons(subdomain, isTestnet(), getNotifyTo());
          if (cronResult.isOpenClaw && cronResult.registered) {
            console.log(chalk.green("  ✓ Participation crons registered"));
          } else if (!cronResult.isOpenClaw) {
            console.log(chalk.dim("  Tip: Set up a scheduled process to run `sherwood session check " + subdomain + "` periodically"));
          }
        } catch { /* non-fatal */ }
        return;
      }

      // Check for existing pending join request
      spinner.text = "Checking pending requests...";
      const pendingRequests = await easLib.queryJoinRequests(fund.creator);
      const existingRequest = pendingRequests.find(
        (r) => r.attester.toLowerCase() === callerAddress.toLowerCase()
          && r.decoded.vault.toLowerCase() === fund.vault.toLowerCase(),
      );
      if (existingRequest) {
        spinner.succeed("You already have a pending join request for this fund");
        console.log(chalk.dim(`  Attestation: ${existingRequest.uid}`));
        console.log(chalk.dim(`  Submitted:   ${new Date(existingRequest.time * 1000).toLocaleString()}`));
        // Still ensure XMTP identity is ready
        try {
          const xmtp = await loadXmtp();
          await xmtp.getXmtpClient();
          console.log(chalk.dim("  XMTP identity ready"));
        } catch {
          console.warn(chalk.yellow("  ⚠ Could not initialize XMTP identity"));
        }
        return;
      }

      spinner.text = "Creating join request attestation...";
      const referrerAgentId = opts.ref ? parseInt(opts.ref, 10) : undefined;
      const { uid, hash } = await easLib.createJoinRequest(
        fund.id,
        BigInt(agentId),
        fund.vault,
        fund.creator,
        opts.message,
        referrerAgentId,
      );

      // Pre-register XMTP identity so the creator can add us to the group on approval
      try {
        spinner.text = "Registering XMTP identity...";
        const xmtp = await loadXmtp();
        await xmtp.getXmtpClient();
        spinner.succeed("Join request created (XMTP identity ready)");
      } catch {
        spinner.succeed("Join request created");
        console.warn(chalk.yellow("  ⚠ Could not initialize XMTP identity — creator may not be able to auto-add you to chat"));
      }

      // Save fund membership + set as primary
      addFund(getChain().id, { subdomain, vault: fund.vault, role: "agent" });
      setPrimaryFund(getChain().id, subdomain);

      // Auto-register participation crons (will HEARTBEAT_OK until approved)
      try {
        const cron = await loadCron();
        const cronResult = cron.registerFundCrons(subdomain, isTestnet(), getNotifyTo());
        if (cronResult.isOpenClaw && cronResult.registered) {
          console.log(G("  ✓ Participation crons registered (will activate after approval)"));
        } else if (!cronResult.isOpenClaw) {
          console.log(DIM("  Tip: Set up a scheduled process to run `sherwood session check " + subdomain + "` periodically"));
        }
      } catch { /* non-fatal */ }

      console.log();
      console.log(LABEL("  ◆ Join Request Submitted"));
      SEP();
      console.log(W(`  Fund:    ${G(`${subdomain}.sherwoodagent.eth`)}`));
      console.log(W(`  Agent ID:     #${agentId}`));
      console.log(W(`  Creator:      ${DIM(fund.creator)}`));
      console.log(W(`  Attestation:  ${DIM(uid)}`));
      console.log(W(`  EAS Scan:     ${DIM(easLib.getEasScanUrl(uid))}`));
      // EAS tx ran on the active chain's EAS deployment (unavailable here => fails cleanly upstream).
      console.log(W(`  Explorer:     ${DIM(easLib.getEasExplorerUrl(hash))}`));
      SEP();
      console.log(G("  ✓ Fund saved to ~/.sherwood/config.json (set as primary)"));
      console.log(G("  ✓ The creator can review with:"));
      console.log(DIM(`    sherwood fund requests --subdomain ${subdomain}`));
      console.log();
    } catch (err) {
      spinner.fail("Join request failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("requests")
  .description("View pending join requests for a fund (creator only)")
  .option("--subdomain <name>", "Fund subdomain")
  .option("--vault <address>", "Vault address (default: from config)")
  .action(async (opts) => {
    const spinner = ora("Loading join requests...").start();
    try {
      let creatorAddress: Address;
      let subdomain: string;
      let fundVault: Address;

      // Resolve fund: --subdomain > primary fund > --vault > config vault
      const resolvedSubdomain = opts.subdomain || getPrimaryFund(getChain().id)?.subdomain;
      if (resolvedSubdomain) {
        const fundInfo = await resolveFund(resolvedSubdomain);
        creatorAddress = fundInfo.creator;
        subdomain = resolvedSubdomain;
        fundVault = fundInfo.vault as Address;
      } else {
        resolveVault(opts);
        fundVault = vaultLib.getVaultAddress();
        const fundInfo = await resolveVaultFund(fundVault);
        creatorAddress = fundInfo.creator;
        subdomain = fundInfo.subdomain;
      }

      // Verify caller is creator
      const callerAddress = getAccount().address.toLowerCase();
      if (creatorAddress.toLowerCase() !== callerAddress) {
        spinner.fail("Only the fund creator can view join requests");
        process.exit(1);
      }

      spinner.text = "Querying EAS attestations...";
      const [allRequests, approvals] = await Promise.all([
        easLib.queryJoinRequests(creatorAddress),
        easLib.queryApprovals(creatorAddress),
      ]);

      // Filter out agents that have already been approved for the same vault
      const approvedKeys = new Set(
        approvals
          .filter((a) => a.decoded.vault.toLowerCase() === fundVault.toLowerCase())
          .map((a) => a.decoded.agentId.toString()),
      );
      const requests = allRequests.filter(
        (r) => !approvedKeys.has(r.decoded.agentId.toString())
          && r.decoded.vault.toLowerCase() === fundVault.toLowerCase(),
      );

      spinner.stop();

      if (requests.length === 0) {
        console.log(DIM("\n  No pending join requests.\n"));
        return;
      }

      console.log();
      console.log(LABEL(`  ◆ Pending Join Requests (${requests.length})`));
      SEP();

      for (let i = 0; i < requests.length; i++) {
        const req = requests[i];
        const date = new Date(req.time * 1000).toLocaleString();
        const referrer = easLib.parseReferrer(req.decoded.message);
        const cleanMessage = easLib.stripReferrerPrefix(req.decoded.message);
        console.log(W(`  ${i + 1}. Agent #${req.decoded.agentId} ${DIM(`(${req.attester})`)}`));
        console.log(DIM(`     Message:     "${cleanMessage}"`));
        if (referrer !== null) {
          console.log(G(`     Referred by: Agent #${referrer}`));
        }
        console.log(DIM(`     Requested:   ${date}`));
        console.log(DIM(`     Attestation: ${req.uid}`));
        console.log();
      }

      console.log(G("  To approve:"));
      console.log(DIM(`    sherwood fund approve --agent-id <id> --wallet <addr>`));
      console.log(G("  To reject:"));
      console.log(DIM(`    sherwood fund reject --attestation <uid>`));
      console.log();
    } catch (err) {
      spinner.fail("Failed to load requests");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("approve")
  .description("Approve an agent join request (registers agent + creates EAS approval)")
  .option("--vault <address>", "Vault address (default: from config)")
  .option("--subdomain <name>", "Fund subdomain (alternative to --vault)")
  .requiredOption("--agent-id <id>", "Agent's ERC-8004 identity token ID")
  .requiredOption("--wallet <address>", "Agent wallet address")
  .action(async (opts) => {
    const spinner = ora("Verifying creator...").start();
    try {
      // Resolve vault: --subdomain > primary fund > --vault > config vault
      const approveSubdomain = opts.subdomain || getPrimaryFund(getChain().id)?.subdomain;
      if (approveSubdomain && !opts.vault) {
        const fundInfo = await resolveFund(approveSubdomain);
        vaultLib.setVaultAddress(fundInfo.vault);
      } else {
        resolveVault(opts);
      }
      const vaultAddress = vaultLib.getVaultAddress();

      const agentWallet = validateAddress(opts.wallet, "wallet");

      // Verify caller is creator
      const { creator, subdomain, id: fundId } = await resolveVaultFund(vaultAddress);

      // --calldata-only: emit registerAgent (fund chain) + AGENT_APPROVED
      // attest (Base) before any signer-dependent path.
      if (isCalldataOnly()) {
        spinner.stop();
        emitCalldata(
          encodeApproveAgent(
            {
              fundId: fundId,
              agentId: BigInt(opts.agentId),
              vault: vaultAddress,
              agentAddress: agentWallet,
            },
            getChain().id as ChainId,
          ),
        );
        return;
      }

      const callerAddress = getAccount().address.toLowerCase();
      if (creator.toLowerCase() !== callerAddress) {
        spinner.fail("Only the fund creator can approve agents");
        process.exit(1);
      }

      // 1. Register agent on-chain (same as fund add)
      spinner.text = "Registering agent on vault...";
      let agentWasRegistered = false;
      try {
        const regHash = await vaultLib.registerAgent(
          BigInt(opts.agentId),
          agentWallet,
        );
        agentWasRegistered = true;
        console.log(DIM(`  Agent registered: ${getExplorerUrl(regHash)}`));
      } catch (regErr) {
        const msg = regErr instanceof Error ? regErr.message : String(regErr);
        if (msg.includes("0xe098d3ee") || msg.includes("AgentAlreadyRegistered")) {
          console.log(DIM("  Agent already registered on vault — skipping"));
        } else {
          throw regErr;
        }
      }

      // Brief delay after on-chain registration to let the RPC node sync its nonce
      if (agentWasRegistered) {
        await new Promise((r) => setTimeout(r, 2000));
      }

      // 2. Create AGENT_APPROVED attestation (skip if one already exists)
      spinner.text = "Checking for existing approval...";
      const existingApprovals = await easLib.queryApprovals(getAccount().address);
      const alreadyApproved = existingApprovals.find(
        (a) => a.decoded.agentId === BigInt(opts.agentId) && a.decoded.vault.toLowerCase() === vaultAddress.toLowerCase(),
      );

      let approvalUid: `0x${string}`;
      if (alreadyApproved) {
        approvalUid = alreadyApproved.uid;
        console.log(DIM(`  Approval attestation already exists — skipping`));
      } else {
        spinner.text = "Creating approval attestation...";
        const result = await easLib.createApproval(
          fundId,
          BigInt(opts.agentId),
          vaultAddress,
          agentWallet,
        );
        approvalUid = result.uid;
      }

      // 3. Auto-add agent to XMTP chat group
      try {
        spinner.text = "Adding to chat...";
        const xmtp = await loadXmtp();
        const xmtpClient = await xmtp.getXmtpClient();
        const group = await xmtp.getGroup(xmtpClient, subdomain);
        await xmtp.addMember(group, opts.wallet);
        await xmtp.sendEnvelope(group, {
          type: "AGENT_REGISTERED",
          agent: { erc8004Id: Number(opts.agentId), address: opts.wallet },
          fund: subdomain,
          timestamp: Math.floor(Date.now() / 1000),
        });
        console.log(DIM(`  Added to chat: ${subdomain}`));
      } catch {
        console.warn(chalk.yellow("  ⚠ Could not add agent to chat group"));
        console.warn(chalk.dim(`    If no group exists, run: sherwood chat ${subdomain} init`));
      }

      spinner.succeed("Agent approved and registered");
      console.log();
      console.log(LABEL("  ◆ Agent Approved"));
      SEP();
      console.log(W(`  Agent ID:     #${opts.agentId}`));
      console.log(W(`  Wallet:       ${G(opts.wallet)}`));
      console.log(W(`  Approval:     ${DIM(approvalUid)}`));
      console.log(W(`  EAS Scan:     ${DIM(easLib.getEasScanUrl(approvalUid))}`));
      SEP();
    } catch (err) {
      spinner.fail("Approval failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("reject")
  .description("Reject a join request by revoking its attestation")
  .requiredOption("--attestation <uid>", "Join request attestation UID to revoke")
  .action(async (opts) => {
    const spinner = ora("Revoking attestation...").start();
    try {
      // Revoke on the chain's EAS deployment — join attestations live on
      // the EAS chain, not the fund's chain.
      const hash = await easLib.revokeJoinRequest(opts.attestation as `0x${string}`);
      spinner.succeed("Join request rejected");
      console.log(DIM(`  ${easLib.getEasExplorerUrl(hash)}`));
    } catch (err) {
      spinner.fail("Rejection failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

fund
  .command("leave")
  .description("Leave a fund — removes participation crons and session state")
  .option("--subdomain <name>", "Fund subdomain to leave")
  .action(async (opts) => {
    const subdomain = opts.subdomain || getPrimaryFund(getChain().id)?.subdomain;
    if (!subdomain) {
      console.error(chalk.red("  No fund configured. Pass --subdomain <name>."));
      process.exit(1);
    }
    const spinner = ora("Cleaning up...").start();
    try {
      // 1. Remove participation crons (if OpenClaw)
      let cronsRemoved = false;
      try {
        const cron = await loadCron();
        const result = cron.unregisterFundCrons(subdomain, isTestnet());
        cronsRemoved = result.removed;
      } catch { /* non-fatal */ }

      // 2. Reset session state
      const { resetSession } = await import("./lib/session.js");
      resetSession(subdomain);

      spinner.succeed("Left fund");
      if (cronsRemoved) {
        console.log(G("  ✓ Participation crons removed"));
      }
      console.log(G("  ✓ Session state cleared"));
      console.log();
      console.log(chalk.dim("  Note: This does not remove you on-chain. To exit your position:"));
      console.log(chalk.dim("    sherwood vault balance   — check your LP share balance"));
      console.log(chalk.dim("    Redeem shares via the vault contract or dashboard"));
      console.log();
    } catch (err) {
      spinner.fail("Leave failed");
      console.error(chalk.red(err instanceof Error ? err.message : String(err)));
      process.exit(1);
    }
  });

fund
  .command("set-primary")
  .description("Set the active fund for CLI commands (auto-used when --subdomain is omitted)")
  .option("--subdomain <name>", "Fund subdomain")
  .option("--vault <address>", "Vault address")
  .action(async (opts) => {
    try {
      const chainId = getChain().id;
      const memberships = getFunds(chainId);

      if (!opts.subdomain && !opts.vault) {
        // List funds and show which is primary
        const primary = getPrimaryFund(chainId);
        if (memberships.length === 0) {
          console.log(DIM("\n  No funds saved. Create or join one first.\n"));
          return;
        }
        console.log();
        console.log(LABEL("  ◆ Your Funds"));
        SEP();
        for (const m of memberships) {
          const isPrimary = primary?.subdomain === m.subdomain;
          const marker = isPrimary ? G(" ★") : "";
          console.log(W(`  ${m.subdomain}.sherwoodagent.eth${marker}  ${DIM(`(${m.role})`)}`));
          console.log(DIM(`    vault: ${m.vault}`));
        }
        SEP();
        if (memberships.length > 1) {
          console.log(DIM("  Switch with: sherwood fund set-primary --subdomain <name>"));
        }
        console.log();
        return;
      }

      let target: string | undefined;

      if (opts.subdomain) {
        // Verify it's in our list
        const found = memberships.find((m) => m.subdomain === opts.subdomain);
        if (!found) {
          // Resolve on-chain to verify it exists, then add it
          const fundInfo = await resolveFund(opts.subdomain);
          addFund(chainId, { subdomain: opts.subdomain, vault: fundInfo.vault, role: "agent" });
        }
        target = opts.subdomain;
      } else if (opts.vault) {
        // Find by vault address
        const found = memberships.find(
          (m) => m.vault.toLowerCase() === opts.vault.toLowerCase(),
        );
        if (found) {
          target = found.subdomain;
        } else {
          // Resolve on-chain
          const fundInfo = await resolveVaultFund(validateAddress(opts.vault, "vault"));
          addFund(chainId, { subdomain: fundInfo.subdomain, vault: opts.vault, role: "agent" });
          target = fundInfo.subdomain;
        }
      }

      if (!target) {
        console.error(chalk.red("  Could not resolve fund. Pass --subdomain or --vault."));
        process.exit(1);
      }

      setPrimaryFund(chainId, target);
      console.log(G(`\n  ✓ Primary fund set to ${target}.sherwoodagent.eth\n`));
    } catch (err) {
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

// ── Vault commands ──
const vaultCmd = program.command("vault");

vaultCmd
  .command("deposit")
  .description("Deposit into a vault")
  .option("--vault <address>", "Vault address (default: from config)")
  .requiredOption("--amount <amount>", "Amount to deposit (in asset units)")
  .option("--use-eth", "Auto-wrap ETH → WETH before depositing (for WETH vaults)")
  .option("--receiver <address>", "Depositor / share receiver (required with --calldata-only)")
  .action(async (opts) => {
    resolveVault(opts);
    const decimals = await vaultLib.getAssetDecimals();
    const amount = parseUnits(opts.amount, decimals);

    // --calldata-only: emit (optional wrap) + approve + deposit calldata. Needs
    // an explicit --receiver (no wallet to infer it from). With --use-eth on a
    // WETH vault, a WETH.deposit{value:shortfall}() wrap leg is prepended; the
    // shortfall is computed from the receiver's current WETH balance.
    if (isCalldataOnly()) {
      if (!opts.receiver) {
        console.error(
          chalk.red(
            "--calldata-only requires --receiver <address> (the depositor / share receiver)",
          ),
        );
        process.exit(1);
      }
      const receiver = validateAddress(opts.receiver, "receiver");
      const asset = await vaultLib.getAssetAddress();
      const assetSymbol = (await getPublicClient().readContract({
        address: asset,
        abi: ERC20_ABI,
        functionName: "symbol",
      })) as string;
      let currentWethBalance: bigint | undefined;
      if (opts.useEth) {
        currentWethBalance = (await getPublicClient().readContract({
          address: asset,
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [receiver],
        })) as bigint;
      }
      try {
        emitCalldata(
          encodeDeposit(
            {
              vault: vaultLib.getVaultAddress(),
              receiver,
              asset,
              assetSymbol,
              assetDecimals: decimals,
              assets: amount,
              wrapEth: Boolean(opts.useEth),
              currentWethBalance,
            },
            getChain().id as ChainId,
          ),
        );
      } catch (err) {
        // e.g. --use-eth on a non-WETH vault — the encoder rejects it.
        console.error(chalk.red(err instanceof Error ? err.message : String(err)));
        process.exit(1);
      }
      return;
    }

    // Pre-flight checks — catch issues before submitting transactions
    try {
      if (!opts.useEth) {
        await vaultLib.preflightDeposit(amount);
      }
    } catch (err) {
      console.error(chalk.red(`\n  ✖ ${err instanceof Error ? err.message : String(err)}\n`));
      process.exit(1);
    }

    const spinner = ora(`Depositing ${opts.amount}...`).start();
    try {
      let hash;
      if (opts.useEth) {
        spinner.text = `Wrapping ETH + depositing ${opts.amount}...`;
        hash = await vaultLib.depositWithEthWrap(amount);
      } else {
        hash = await vaultLib.deposit(amount);
      }
      spinner.succeed(`Deposited: ${hash}`);
      console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
    } catch (err) {
      spinner.fail("Deposit failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

vaultCmd
  .command("info")
  .description("Display vault state")
  .option("--vault <address>", "Vault address (default: from config)")
  .action(async (opts) => {
    resolveVault(opts);
    const spinner = ora("Loading vault info...").start();
    try {
      const info = await vaultLib.getVaultInfo();
      spinner.stop();
      console.log();
      console.log(chalk.bold("Vault Info"));
      console.log(chalk.dim("─".repeat(40)));
      console.log(`  Address:            ${info.address}`);
      console.log(`  Total Assets:       ${info.totalAssets}`);
      console.log(`  Agent Count:        ${info.agentCount}`);
      console.log(`  Redemptions Locked: ${info.redemptionsLocked}`);
      console.log(`  Management Fee:     ${Number(info.managementFeeBps) / 100}%`);
      console.log();
    } catch (err) {
      spinner.fail("Failed to load vault info");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

vaultCmd
  .command("balance")
  .description("Show LP share balance and asset value")
  .option("--vault <address>", "Vault address (default: from config)")
  .option("--address <address>", "Address to check (default: your wallet)")
  .action(async (opts) => {
    resolveVault(opts);
    const spinner = ora("Loading balance...").start();
    try {
      const balance = await vaultLib.getBalance(opts.address as Address | undefined);
      spinner.stop();
      console.log();
      console.log(chalk.bold("LP Position"));
      console.log(chalk.dim("─".repeat(40)));
      console.log(`  Shares:       ${balance.shares.toString()}`);
      console.log(`  Asset Value:  ${balance.assetsValue}`);
      console.log(`  % of Vault:   ${balance.percentOfVault}`);
      console.log();
    } catch (err) {
      spinner.fail("Failed to load balance");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

vaultCmd
  .command("redeem")
  .description("Redeem vault shares for the underlying asset (ERC-4626)")
  .option("--vault <address>", "Vault address (default: from config)")
  .option("--shares <amount>", "Shares to redeem in whole-share units (default: all)")
  .option("--receiver <address>", "Receiver of the underlying asset (default: your wallet)")
  .option("--owner <address>", "Share owner whose shares burn (required with --calldata-only)")
  .action(async (opts) => {
    resolveVault(opts);

    // Vault shares use (assetDecimals + _decimalsOffset) where offset = assetDecimals.
    // For USDC (6) this is 12. Treat --shares as whole-share units and scale by decimals * 2.
    const assetDecimals = await vaultLib.getAssetDecimals();
    const shareDecimals = assetDecimals * 2;

    // --calldata-only: emit redeem calldata. Needs explicit --shares (auto-"all"
    // reads your on-chain balance) and --owner (the share holder).
    if (isCalldataOnly()) {
      if (!opts.shares) {
        console.error(
          chalk.red(
            "--calldata-only requires --shares (auto-'all' needs your on-chain share balance)",
          ),
        );
        process.exit(1);
      }
      if (!opts.owner) {
        console.error(
          chalk.red(
            "--calldata-only requires --owner <address> (the share holder whose shares burn)",
          ),
        );
        process.exit(1);
      }
      const owner = validateAddress(opts.owner, "owner");
      const receiver = opts.receiver
        ? validateAddress(opts.receiver, "receiver")
        : owner;
      emitCalldata(
        encodeRedeem(
          {
            vault: vaultLib.getVaultAddress(),
            receiver,
            owner,
            shares: parseUnits(opts.shares, shareDecimals),
          },
          getChain().id as ChainId,
        ),
      );
      return;
    }

    let shares: bigint;
    try {
      if (opts.shares) {
        shares = parseUnits(opts.shares, shareDecimals);
      } else {
        shares = await vaultLib.getShareBalance();
        if (shares === 0n) {
          console.error(chalk.red("\n  ✖ No shares to redeem.\n"));
          process.exit(1);
        }
      }
    } catch (err) {
      console.error(chalk.red(`\n  ✖ ${err instanceof Error ? err.message : String(err)}\n`));
      process.exit(1);
    }

    if (opts.receiver && !isAddress(opts.receiver)) {
      console.error(chalk.red(`\n  ✖ Invalid receiver address: ${opts.receiver}\n`));
      process.exit(1);
    }

    try {
      await vaultLib.preflightRedeem(shares);
    } catch (err) {
      console.error(chalk.red(`\n  ✖ ${err instanceof Error ? err.message : String(err)}\n`));
      process.exit(1);
    }

    const sharesDisplay = formatUnits(shares, shareDecimals);
    const spinner = ora(`Redeeming ${sharesDisplay} shares...`).start();
    try {
      const { hash, assetsOut } = await vaultLib.redeem(
        shares,
        opts.receiver as Address | undefined,
      );
      spinner.succeed(`Redeemed: ${hash}`);
      console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
      console.log(`  Assets received: ${formatUnits(assetsOut, assetDecimals)}`);
    } catch (err) {
      spinner.fail("Redeem failed");
      console.error(chalk.red(formatContractError(err)));
      process.exit(1);
    }
  });

// ── Strategy commands ──
const strategy = program.command("strategy").description("Strategy templates — list, clone, propose");
registerStrategyTemplateCommands(strategy);

// ── Provider info ──
program
  .command("providers")
  .description("List available DeFi providers")
  .action(async () => {
    const { MessariProvider, NansenProvider } = await import("./providers/research/index.js");
    const providers = [new MoonwellProvider(), new UniswapProvider(), new MessariProvider(), new NansenProvider()];
    for (const p of providers) {
      const info = p.info();
      console.log(`\n${info.name} (${info.type})`);
      console.log(`  Capabilities: ${info.capabilities.join(", ")}`);
      console.log(`  Chains: ${info.supportedChains.map((c) => c.name).join(", ")}`);
    }
  });

// ── Chat commands (lazy-loaded — requires @xmtp/cli binary) ──
try {
  const { registerChatCommands } = await import("./commands/chat.js");
  registerChatCommands(program);
} catch {
  program
    .command("chat <name> [action] [actionArgs...]")
    .description("Fund chat (XMTP) — requires @xmtp/cli")
    .action(() => {
      console.error(chalk.red("XMTP CLI not available."));
      console.error(chalk.dim("Install with:  npm install -g @xmtp/cli"));
      console.error(chalk.dim("Or reinstall:  npm i -g @sherwoodagent/cli"));
      process.exit(1);
    });
}

// ── Session commands ──
const { registerSessionCommands } = await import("./commands/session.js");
registerSessionCommands(program);

// ── Venice commands ──
registerVeniceCommands(program);

// ── Allowance commands ──
registerAllowanceCommands(program);

// ── Identity commands ──
registerIdentityCommands(program);

// ── Proposal commands ──
registerProposalCommands(program);

// ── Lane B async queue commands ──
registerQueueCommands(program);

// ── Governor commands ──
registerGovernorCommands(program);

// ── Guardian delegation commands (V1.5) ──
registerGuardianCommands(program);

// ── Research commands ──
const { registerResearchCommands } = await import("./commands/research.js");
registerResearchCommands(program);

// ── Agent commands ──
const { registerAgentCommands } = await import("./commands/agent.js");
registerAgentCommands(program);

// ── Trade commands ──
const { registerTradeCommands } = await import("./commands/trade.js");
registerTradeCommands(program);

// ── Grid commands ──
import { registerGridCommand } from './commands/grid.js';
registerGridCommand(program);

// ── Config commands ──
const configCmd = program.command("config");

configCmd
  .command("set")
  .description("Save settings to ~/.sherwood/config.json (persists across sessions)")
  .option("--private-key <key>", "Wallet private key (0x-prefixed)")
  .option("--vault <address>", "Default vault address")
  .option("--rpc <url>", "Custom RPC URL for the active --chain network")
  .option("--notify-to <id>", "Destination for cron summaries (Telegram chat ID, phone, etc.)")
  .option("--uniswap-api-key <key>", "Uniswap Trading API key (from developers.uniswap.org)")
  .option("--venice-api-key <key>", "Venice AI inference API key")
  .option("--anthropic-api-key <key>", "Anthropic Claude API key (for LLM judge)")
  .option("--xmtp-group <subdomain:groupId>", "Cache an XMTP group ID for a fund (e.g. my-fund:abc123)")
  .action((opts) => {
    let saved = false;

    if (opts.privateKey) {
      setPrivateKey(opts.privateKey);
      const account = getAccount();
      console.log(chalk.green("Private key saved to ~/.sherwood/config.json"));
      console.log(chalk.dim(`  Wallet: ${account.address}`));
      saved = true;
    }

    if (opts.vault) {
      const chainId = getChain().id;
      setChainContract(chainId, "vault", opts.vault);
      console.log(chalk.green(`Vault saved to ~/.sherwood/config.json (chain ${chainId})`));
      console.log(chalk.dim(`  Vault: ${opts.vault}`));
      saved = true;
    }

    if (opts.rpc) {
      const network = getNetwork();
      setConfigRpcUrl(network, opts.rpc);
      console.log(chalk.green(`RPC URL saved for ${network}`));
      console.log(chalk.dim(`  RPC: ${opts.rpc}`));
      saved = true;
    }

    if (opts.notifyTo) {
      setNotifyTo(opts.notifyTo);
      console.log(chalk.green("Notify destination saved to ~/.sherwood/config.json"));
      console.log(chalk.dim(`  Notify to: ${opts.notifyTo}`));
      saved = true;
    }

    if (opts.uniswapApiKey) {
      setUniswapApiKey(opts.uniswapApiKey);
      console.log(chalk.green("Uniswap API key saved to ~/.sherwood/config.json"));
      saved = true;
    }

    if (opts.veniceApiKey) {
      setVeniceApiKey(opts.veniceApiKey);
      console.log(chalk.green("Venice API key saved to ~/.sherwood/config.json"));
      saved = true;
    }

    if (opts.anthropicApiKey) {
      setAnthropicApiKey(opts.anthropicApiKey);
      console.log(chalk.green("Anthropic API key saved to ~/.sherwood/config.json"));
      saved = true;
    }

    if (opts.xmtpGroup) {
      const parts = opts.xmtpGroup.split(":");
      if (parts.length !== 2 || !parts[0] || !parts[1]) {
        console.log(chalk.red("Format: --xmtp-group <subdomain>:<groupId>"));
        process.exit(1);
      }
      cacheGroupId(parts[0], parts[1]);
      console.log(chalk.green(`XMTP group ID cached for ${parts[0]}`));
      console.log(chalk.dim(`  Group ID: ${parts[1]}`));
      saved = true;
    }

    if (!saved) {
      console.log(chalk.red("Provide at least one of: --private-key, --vault, --rpc, --notify-to, --uniswap-api-key, --venice-api-key, --anthropic-api-key, --xmtp-group"));
      process.exit(1);
    }
  });

configCmd
  .command("show")
  .description("Display current config for the active network")
  .action(() => {
    const network = getNetwork();
    const chainId = getChain().id;
    const contracts = getChainContracts(chainId);
    const config = loadConfig();
    const customRpc = config.rpc?.[network];

    console.log();
    console.log(chalk.bold(`Sherwood Config`));
    console.log(chalk.dim("─".repeat(50)));
    console.log(`  Network:    ${chalk.cyan(network)} (chain ${chainId})`);
    console.log(`  RPC:        ${customRpc ? chalk.green(customRpc) : chalk.dim("default")}`);
    console.log(`  Wallet:     ${config.privateKey ? chalk.green("configured") : chalk.dim("not set")}`);
    console.log(`  Agent ID:   ${config.agentId ?? chalk.dim("not set")}`);
    console.log(`  Vault:      ${contracts.vault ?? chalk.dim("not set")}`);
    console.log(`  Uniswap:    ${getUniswapApiKey() ? chalk.green("API key configured") : chalk.dim("not set")}`);
    console.log(`  Venice:     ${getVeniceApiKey() ? chalk.green("API key configured") : chalk.dim("not set")}`);

    // Show XMTP group IDs
    const groupEntries = Object.entries(config.groupCache || {}).filter(([, v]) => v);
    if (groupEntries.length > 0) {
      console.log();
      console.log(chalk.bold("  XMTP Groups"));
      for (const [sub, gid] of groupEntries) {
        console.log(`    ${sub}: ${chalk.cyan(gid)}`);
      }
    }

    console.log();
    console.log(chalk.dim("  Config file: ~/.sherwood/config.json"));
    console.log();
  });

program.parse();
